/**
 * The social product's routes (lane ALGOBOO2, docs/API.md "The social product"): the front page ranked by today's
 * numbers, shadowing an agent with play money, remixing one, the daily story, the team an agent is and friendly
 * challenges. Each reads the platform through the App API; nothing here keeps a book of its own.
 */
import { z } from 'zod'
import { AgentLineage, AgentStory, AgentTeam, ChallengeCreate, ChallengeEntryAdd, ChallengeList, ChallengeView, FrontPage, RemixCreate, RemixOptions, RemixResult, RuleActivate, RuleActivated, RuleProposal, RuleProposalRequest, ShadowStart, ShadowUpgrade, ShadowView } from '../product.js'
import { HandleParams, IdParams } from './params.js'
import { defineRoute, type RouteDef } from './define.js'

const ShadowParams = z.object({ ref: z.string().regex(/^shd_[0-9a-z]{16,40}$/) })

export const productRoutes = {
  // --- the front page (FOMO1) ---
  getFront: defineRoute({
    method: 'GET',
    path: '/front',
    auth: 'public',
    summary: "The front page: agents ranked by today's results, agent tokens by price and market cap, gainers, new launches and live trade cards with their receipts",
    response: FrontPage,
  }),

  // --- shadowing an agent with play money (SHADOW1) ---
  startShadow: defineRoute({
    method: 'POST',
    path: '/agents/:handle/shadow',
    auth: 'session',
    summary: "Shadow the agent with play money at your own size and delay: its trades copied through the platform's paper fill model, simulated",
    params: HandleParams,
    body: ShadowStart,
    status: 201,
    response: ShadowView,
  }),
  listMyShadows: defineRoute({ method: 'GET', path: '/me/shadows', auth: 'session', summary: 'Your shadows, newest first (terms only)', response: z.array(ShadowView) }),
  getMyShadow: defineRoute({
    method: 'GET',
    path: '/me/shadows/:ref',
    auth: 'session',
    summary: 'One of your shadows with what it would have made, simulated now',
    params: ShadowParams,
    response: ShadowView,
  }),
  stopMyShadow: defineRoute({ method: 'POST', path: '/me/shadows/:ref/stop', auth: 'session', summary: 'Stop a shadow; its result stands', params: ShadowParams, response: ShadowView }),
  upgradeMyShadow: defineRoute({
    method: 'POST',
    path: '/me/shadows/:ref/upgrade',
    auth: 'session',
    summary: 'Turn the shadow into the real permission you granted the agent (it ends upgraded)',
    params: ShadowParams,
    body: ShadowUpgrade,
    response: ShadowView,
  }),

  // --- remixing an agent (REMIX1) ---
  getRemixOptions: defineRoute({
    method: 'GET',
    path: '/agents/:handle/remix',
    auth: 'public',
    summary: "What a remix of the agent may change, in plain words, over its program's declared settings",
    params: HandleParams,
    response: RemixOptions,
  }),
  remixAgent: defineRoute({
    method: 'POST',
    path: '/agents/:handle/remix',
    auth: 'session',
    summary: 'Fork the agent with one behaviour changed: a new draft agent of yours on a fork of its strategy, its lineage kept',
    params: HandleParams,
    body: RemixCreate,
    status: 201,
    response: RemixResult,
  }),
  getAgentLineage: defineRoute({
    method: 'GET',
    path: '/agents/:handle/lineage',
    auth: 'public',
    summary: 'Where the agent came from, its remixes, and both results since the fork',
    params: HandleParams,
    response: AgentLineage,
  }),

  // --- the daily story (STORY1; algo.boo's own since BOOSTORY1) ---
  getAgentStory: defineRoute({
    method: 'GET',
    path: '/agents/:handle/story',
    auth: 'public',
    summary: "The agent's story of a UTC day (yesterday by default): noticed, did, learned, every line citing its records",
    params: HandleParams,
    query: z.object({ day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional() }),
    response: AgentStory,
  }),

  // --- the team an agent is, and teaching it a rule (TEAM1) ---
  getAgentTeam: defineRoute({
    method: 'GET',
    path: '/agents/:handle/team',
    auth: 'public',
    summary: 'The team the agent is -- its trader and the scouts whose lists it reads -- and who did what in its recent decisions',
    params: HandleParams,
    response: AgentTeam,
  }),
  proposeAgentRule: defineRoute({
    method: 'POST',
    path: '/agents/:handle/rules/propose',
    auth: 'session',
    owner: true,
    summary: "A rule in your words shown exactly -- the settings it changes, or the program line it needs -- with last week's decisions under it (owner)",
    params: HandleParams,
    body: RuleProposalRequest,
    response: RuleProposal,
  }),
  activateAgentRule: defineRoute({
    method: 'POST',
    path: '/agents/:handle/rules/activate',
    auth: 'session',
    owner: true,
    summary: "Make a shown rule the agent's next version (owner)",
    params: HandleParams,
    body: RuleActivate,
    status: 201,
    response: RuleActivated,
  }),

  // --- friendly challenges (CHALLENGE1) ---
  createChallenge: defineRoute({
    method: 'POST',
    path: '/challenges',
    auth: 'session',
    summary: 'Put two to eight agents side by side over the next UTC week with the same play money',
    body: ChallengeCreate,
    status: 201,
    response: ChallengeView,
  }),
  getChallenge: defineRoute({
    method: 'GET',
    path: '/challenges/:id',
    auth: 'public',
    summary: "A challenge's scoreboard: each agent's week -- result, costs, worst drop, days sat out -- and its return on the play money",
    params: IdParams,
    response: ChallengeView,
  }),
  addChallengeEntry: defineRoute({
    method: 'POST',
    path: '/challenges/:id/entries',
    auth: 'session',
    summary: 'Add one more agent to a challenge before its week starts',
    params: IdParams,
    body: ChallengeEntryAdd,
    status: 201,
    response: ChallengeView,
  }),
  listMyChallenges: defineRoute({
    method: 'GET',
    path: '/me/challenges',
    auth: 'session',
    summary: 'The challenges you made or have an agent in',
    response: ChallengeList,
  }),
} as const satisfies Record<string, RouteDef>
